import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import SwapHorizOutlinedIcon from '@mui/icons-material/SwapHorizOutlined';
import {
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';

type Props = {
  purchaseCanCreate?: boolean;
  onStartPurchase?: () => void;
  purchaseLoading?: boolean;
  hasPurchaseApplication?: boolean;
  purchaseIntentPhrase?: string;
  onStartRefinance?: () => void;
  refinanceLoading?: boolean;
  hasRefinanceApplication?: boolean;
};

export function LoanHubFlowGuide({
  purchaseCanCreate = false,
  onStartPurchase,
  purchaseLoading = false,
  hasPurchaseApplication = false,
  purchaseIntentPhrase = 'purchase',
  onStartRefinance,
  refinanceLoading = false,
  hasRefinanceApplication = false,
}: Props) {
  const theme = useTheme();

  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      <Grid item xs={12} md={6}>
        <Card
          variant="outlined"
          className="taste-panel taste-rise"
          sx={{
            height: '100%',
            borderColor: alpha(theme.palette.primary.main, 0.35),
            bgcolor: alpha(theme.palette.primary.main, 0.04),
          }}
        >
          <CardContent>
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{ mb: 1 }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  color: theme.palette.primary.main,
                }}
              >
                <HomeOutlinedIcon />
              </Box>
              <Typography variant="subtitle1" fontWeight={700}>
                Purchase applications
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {purchaseCanCreate
                ? hasPurchaseApplication
                  ? `Your ${purchaseIntentPhrase} application is in progress. Property details for listings are edited in Deal Room → Stage 3.`
                  : `Create your ${purchaseIntentPhrase} master file while you shop. Link saved properties from the dashboard.`
                : 'Use one master file while you shop.'}
            </Typography>
            {purchaseCanCreate && onStartPurchase ? (
              <Button
                variant="contained"
                size="small"
                disabled={purchaseLoading}
                onClick={onStartPurchase}
              >
                {hasPurchaseApplication
                  ? `Continue ${purchaseIntentPhrase} application`
                  : `Start ${purchaseIntentPhrase} application`}
              </Button>
            ) : null}
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card
          variant="outlined"
          className="taste-panel taste-rise-delay-1"
          sx={{
            height: '100%',
            borderColor: alpha(theme.palette.info.main, 0.35),
            bgcolor: alpha(theme.palette.info.main, 0.04),
          }}
        >
          <CardContent>
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{ mb: 1 }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: alpha(theme.palette.info.main, 0.12),
                  color: theme.palette.info.main,
                }}
              >
                <SwapHorizOutlinedIcon />
              </Box>
              <Typography variant="subtitle1" fontWeight={700}>
                Refinance applications
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              One application for all properties and loan options you are
              comparing numbers for.
            </Typography>
            {onStartRefinance ? (
              <Button
                variant="contained"
                color="info"
                size="small"
                disabled={refinanceLoading}
                onClick={onStartRefinance}
              >
                {hasRefinanceApplication
                  ? 'Continue refinance application'
                  : 'Start refinance application'}
              </Button>
            ) : null}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}
